// Game mode ids, their wire codes and URL hashes (mechanics §17, protocol §5.3, ui-flow §3).

export const MODE = {
  FFA: 'ffa',
  PARTY: 'party',
  TEAMS: 'teams',
  EXPERIMENTAL: 'experimental',
  BATTLE_ROYALE: 'battleroyale',
  TEAM_RUSH: 'teamrush',
  RUSH: 'rush',
} as const;

export type ModeId = (typeof MODE)[keyof typeof MODE];

/** Numeric mode codes used in WELCOME and MATCH_RESULT (mechanics §17). */
export const MODE_CODE = {
  ffa: 0,
  party: 1,
  teams: 2,
  experimental: 3,
  battleroyale: 4,
  teamrush: 5,
  rush: 6,
} as const satisfies Record<ModeId, number>;

export type ModeCode = (typeof MODE_CODE)[ModeId];

/** Every mode id, in wire-code order. */
export const MODE_IDS: readonly ModeId[] = [
  MODE.FFA,
  MODE.PARTY,
  MODE.TEAMS,
  MODE.EXPERIMENTAL,
  MODE.BATTLE_ROYALE,
  MODE.TEAM_RUSH,
  MODE.RUSH,
];

/** The four public mode tiles, in the order the "Pick a mode" card shows them (ui-flow §4.3). */
export const PUBLIC_MODE_TILES = [
  MODE.FFA,
  MODE.BATTLE_ROYALE,
  MODE.TEAMS,
  MODE.EXPERIMENTAL,
] as const satisfies readonly ModeId[];

export type PublicModeTile = (typeof PUBLIC_MODE_TILES)[number];

/** URL hashes that select a mode (ui-flow §3). Party uses a code; Team Rush and Rush have none. */
export const MODE_HASH = {
  ffa: '#ffa',
  teams: '#teams',
  experimental: '#experimental',
  battleroyale: '#battleroyale',
} as const satisfies Partial<Record<ModeId, string>>;

/** Which milestone ships each mode (mechanics §17); `null` = optional variant, no milestone. */
export const MODE_MILESTONE: Readonly<Record<ModeId, 1 | 3 | null>> = {
  ffa: 1,
  party: 3,
  teams: 3,
  experimental: 3,
  battleroyale: 3,
  teamrush: null,
  rush: null,
};

export function isModeId(v: unknown): v is ModeId {
  return typeof v === 'string' && (MODE_IDS as readonly string[]).includes(v);
}

export function modeCodeOf(id: ModeId): ModeCode {
  return MODE_CODE[id];
}

/** The mode id for a wire code, or `undefined` for an unknown code. */
export function modeIdFromCode(code: number): ModeId | undefined {
  return MODE_IDS[code];
}

/** Battle Royale match states (protocol §5.4 MATCH). */
export const MATCH_STATE = {
  LOBBY: 0,
  COUNTDOWN: 1,
  START: 2,
  BATTLE: 3,
  OVER: 4,
} as const;

export type MatchState = (typeof MATCH_STATE)[keyof typeof MATCH_STATE];

// --- party codes (mechanics §17.2) ---

/** A–Z and 2–9 without I, O, 0 and 1: 32 symbols. */
export const PARTY_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const PARTY_CODE_LENGTH = 6;

/** Upper-cases and trims a code typed or pasted by a user (matching ignores case). */
export function normalizePartyCode(s: string): string {
  return s.trim().toUpperCase();
}

/** True for a well-formed party code. Pass the code through `normalizePartyCode` first. */
export function isPartyCode(s: string): boolean {
  if (s.length !== PARTY_CODE_LENGTH) return false;
  for (const ch of s) if (!PARTY_CODE_ALPHABET.includes(ch)) return false;
  return true;
}

export type HashTarget = { kind: 'mode'; mode: ModeId } | { kind: 'party'; code: string };

/**
 * Reads a URL hash (ui-flow §3): `#ffa`, `#teams`, `#experimental` or `#battleroyale` select a
 * mode, a 6-character code (any case) is a party deep link, anything else is `null` (ignored).
 */
export function parseLocationHash(hash: string): HashTarget | null {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const [mode, h] of Object.entries(MODE_HASH)) {
    if (h.slice(1) === body.toLowerCase()) return { kind: 'mode', mode: mode as ModeId };
  }
  const code = normalizePartyCode(body);
  return isPartyCode(code) ? { kind: 'party', code } : null;
}
